import { useCallback, useEffect, useState } from 'react'
import { ClipboardCheck } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/UiProviders'
import { PERMISSIONS } from '@/lib/permissions'
import { PermissionDenied, ErrorState, Skeleton, EmptyState } from '@/components/States'
import { ApiError } from '@/lib/api'
import {
  fetchAccessRequests,
  approveAccess,
  rejectAccess,
  executeAccess,
  cancelAccess,
  ORG_STR as S,
  REQ_STATUS,
  type AccessReqRow,
} from './staffOrgApi'

/**
 * A23.06 — Huquq berish so'rovlari. JONLI ikki-nazorat (four-eyes) navbati ustidan
 * (GET /admin/four-eyes + approve/reject/execute/cancel). O'z so'rovini tasdiqlash
 * server tomonda taqiqlanadi (403 → fourEyesSelf toast). Majburlash serverda.
 */
export function AccessRequestsScreen() {
  const { can } = useAuth()
  const { toast } = useToast()
  const [status, setStatus] = useState('')
  const [data, setData] = useState<AccessReqRow[] | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [tick, setTick] = useState(0)
  const reload = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    const ac = new AbortController()
    setErr(null)
    setData(null)
    fetchAccessRequests(status, ac.signal)
      .then(setData)
      .catch((e) => {
        if (!ac.signal.aborted)
          setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
      })
    return () => ac.abort()
  }, [tick, status])

  const act = async (fn: () => Promise<unknown>) => {
    try {
      await fn()
      toast(S.done, { tone: 'success' })
      reload()
    } catch (e) {
      toast(e instanceof ApiError && e.isForbidden ? S.fourEyesSelf : S.failed, { tone: 'error' })
    }
  }

  if (!can(PERMISSIONS.PERMISSION_GRANT))
    return <PermissionDenied permission={PERMISSIONS.PERMISSION_GRANT} />

  return (
    <>
      <PageHeader
        title={S.accessReq}
        screenId="A23.06"
        description={S.accessReqDesc}
        verified
      />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto max-w-3xl space-y-4">
          <div className="flex items-center justify-between text-sm">
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="rounded-md border border-border-default bg-surface-2 px-2 py-1 text-secondary"
            >
              <option value="">{S.all}</option>
              <option value="pending">{S.pending}</option>
              <option value="approved">{S.approved}</option>
              <option value="rejected">{S.rejected}</option>
              <option value="executed">{S.executed}</option>
              <option value="cancelled">{S.cancelled}</option>
            </select>
          </div>

          {err ? (
            <ErrorState code={err.code} onRetry={reload} />
          ) : !data ? (
            <div className="space-y-1.5">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : data.length === 0 ? (
            <EmptyState icon={<ClipboardCheck size={28} strokeWidth={1.5} />} title={S.empty} />
          ) : (
            <div className="space-y-2">
              {data.map((r) => {
                const st = REQ_STATUS[r.status]
                return (
                  <div key={r.id} className="rounded-lg border border-border-default bg-surface-2 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-mono text-sm text-primary">{r.action}</span>
                          <span className="font-mono text-xs text-tertiary">{r.target}</span>
                          <span className={`rounded px-1.5 py-0.5 text-xs ${st?.pill ?? ''}`}>
                            {st?.label ?? r.status}
                          </span>
                          <span className="rounded bg-surface-3 px-1.5 py-0.5 text-xs text-tertiary">
                            {S.level} {r.danger_level}
                          </span>
                        </div>
                        {r.reason && <p className="mt-1 text-sm text-secondary">{r.reason}</p>}
                        <p className="mt-0.5 text-xs text-tertiary">
                          {S.requester}: {r.requester || '—'}
                          {r.approver && ` · ${S.approver}: ${r.approver}`}
                          {r.expires_at
                            ? ` · ${S.expires}: ${new Date(r.expires_at).toLocaleString()}`
                            : ''}
                        </p>
                      </div>
                      <div className="flex shrink-0 gap-1.5">
                        {r.status === 'pending' && (
                          <>
                            <button
                              type="button"
                              onClick={() => act(() => approveAccess(r.id, ''))}
                              className="rounded-md bg-success px-2.5 py-1 text-xs text-white"
                            >
                              {S.approve}
                            </button>
                            <button
                              type="button"
                              onClick={() => act(() => rejectAccess(r.id, ''))}
                              className="rounded-md border border-border-default px-2.5 py-1 text-xs text-secondary hover:text-primary"
                            >
                              {S.reject}
                            </button>
                          </>
                        )}
                        {r.status === 'approved' && (
                          <>
                            <button
                              type="button"
                              onClick={() => act(() => executeAccess(r.id))}
                              className="rounded-md bg-accent px-2.5 py-1 text-xs text-white"
                            >
                              {S.execute}
                            </button>
                            <button
                              type="button"
                              onClick={() => act(() => cancelAccess(r.id, ''))}
                              className="rounded-md bg-danger px-2.5 py-1 text-xs text-white"
                            >
                              {S.cancel}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
